import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";

function fmt(n) {
    return new Intl.NumberFormat("en-KE", { maximumFractionDigits: 2 }).format(n || 0);
}

const iconMap = {
    phone: Icons.Phone,
    wifi: Icons.Wifi,
    zap: Icons.Zap,
    tv: Icons.Tv,
    ticket: Icons.Ticket,
    shield: Icons.ShieldCheck,
    "gamepad-2": Icons.Gamepad2,
    receipt: Icons.Receipt,
};

export default function SellTab() {
    const [catalog, setCatalog] = useState([]);
    const [selected, setSelected] = useState(null);
    const [operator, setOperator] = useState("");
    const [amount, setAmount] = useState(0);
    const [recipient, setRecipient] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        api.get("/vas/catalog").then((r) => setCatalog(r.data));
    }, []);

    const open = (p) => {
        setSelected(p);
        setOperator(p.operators[0]);
        setAmount(p.denominations[0]);
        setRecipient("");
    };

    const close = () => setSelected(null);

    const sell = async () => {
        if (!recipient) {
            toast.error("Recipient is required");
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post("/vas/sell", {
                product_code: selected.code,
                operator,
                recipient,
                amount: Number(amount),
            });
            toast.success(`Sale complete. Commission KES ${fmt(data.commission)}. New balance KES ${fmt(data.balance)}`);
            close();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="sell-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-display text-3xl font-extrabold text-onego-navy">Sell digital services</h2>
                    <p className="text-slate-600">Pick a product, enter the recipient, and earn instant commission.</p>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                {catalog.map((p, i) => {
                    const Icon = iconMap[p.icon] || Icons.Sparkles;
                    return (
                        <button
                            key={p.code}
                            onClick={() => open(p)}
                            className="og-card p-6 text-left hover:scale-[1.01] transition-transform"
                            data-testid={`vas-card-${p.code}`}
                        >
                            <div className="h-12 w-12 rounded-2xl og-gradient flex items-center justify-center text-white">
                                <Icon size={22} />
                            </div>
                            <h3 className="mt-4 font-display text-xl font-bold text-onego-navy">{p.name}</h3>
                            <p className="mt-1 text-xs text-slate-500">{p.operators.length} operators &middot; {p.denominations.length} denominations</p>
                        </button>
                    );
                })}
            </div>

            <Dialog open={!!selected} onOpenChange={(o) => !o && close()}>
                <DialogContent data-testid="vas-sell-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl">Sell {selected?.name}</DialogTitle>
                        <DialogDescription>Choose operator, denomination and recipient.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <label className="text-sm font-semibold text-onego-navy">Operator</label>
                            <select className="og-input mt-1" value={operator} onChange={(e) => setOperator(e.target.value)} data-testid="vas-operator-select">
                                {selected?.operators.map((o) => <option key={o}>{o}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-sm font-semibold text-onego-navy">Denomination (KES)</label>
                            <div className="mt-2 flex flex-wrap gap-2">
                                {selected?.denominations.map((d) => (
                                    <button
                                        key={d}
                                        type="button"
                                        onClick={() => setAmount(d)}
                                        className={`px-4 py-2 rounded-full text-sm font-bold border transition ${amount === d ? "bg-onego-navy text-white border-onego-navy" : "border-slate-200 text-slate-700 hover:border-onego-navy"}`}
                                        data-testid={`vas-amount-${d}`}
                                    >
                                        {d.toLocaleString()}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="text-sm font-semibold text-onego-navy">Recipient</label>
                            <input
                                className="og-input mt-1"
                                value={recipient}
                                onChange={(e) => setRecipient(e.target.value)}
                                placeholder={selected?.code === "ELECTRICITY" ? "Meter number" : "Phone number"}
                                data-testid="vas-recipient-input"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <button onClick={close} className="og-btn-ghost" data-testid="vas-cancel-btn">Cancel</button>
                        <button onClick={sell} disabled={loading} className="og-btn-gradient disabled:opacity-60" data-testid="vas-confirm-btn">
                            {loading ? "Processing\u2026" : `Sell KES ${fmt(amount)}`}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
